import { useState } from 'react';
import { Input } from '@/components/ui/input';
import type { CentrumPodpowiedz } from '@/lib/employees-api';
import { useI18n } from '@/lib/i18n';
import { filtrujCentra } from '@/lib/podpowiedzi-centrum';
import { cn } from '@/lib/utils';

/**
 * POLE CENTRUM KOSZTOWEGO Z PODPOWIEDZIAMI.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZGŁOSZONE 01.09.2026: „pole Centrum kosztowe miało podpowiadać rzeczy które są w centrach
 *  kosztowych — teraz jest to zwykłe pole tekstowe i nie podpowiada". Podpowiedzi jechały z serwera
 *  jak trzeba, ale wisiały na `<datalist>`, którego Chrome i Edge nie rozwijają przy
 *  `autocomplete="off"`. Rysujemy listę sami, więc nie zależy od tego, co przeglądarka zrobi
 *  z podpowiedziami — a `autocomplete="off"` musi zostać, bo bez niego pole łapie podpowiedzi
 *  przeglądarki z zupełnie innych formularzy.
 *
 *  ⚠️ POLE PRZYJMUJE DOWOLNY TEKST i to jest wymóg, nie niedbałość: `Users.COSTCENTER` w SPS jest
 *  gołym napisem, słownik centrów u klientów bywa pusty (na DBW zero encji przy 472 osobach
 *  z kodem), więc lista wyłącznie do wyboru odcięłaby zakładanie nowego centrum.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function PoleCentrumKosztowego({
  id,
  wartosc,
  podpowiedzi,
  placeholder,
  onZmiana,
}: {
  id: string;
  wartosc: string;
  podpowiedzi: CentrumPodpowiedz[];
  placeholder?: string;
  onZmiana: (v: string) => void;
}) {
  const { t } = useI18n();
  const [otwarte, setOtwarte] = useState(false);
  const [wybrany, setWybrany] = useState(-1);

  const dopasowane = filtrujCentra(podpowiedzi, wartosc);
  const widoczne = otwarte && dopasowane.length > 0;

  const wybierz = (code: string) => {
    onZmiana(code);
    setOtwarte(false);
    setWybrany(-1);
  };

  return (
    <div className="relative">
      <Input
        id={id}
        value={wartosc}
        maxLength={50}
        autoComplete="off"
        role="combobox"
        aria-expanded={widoczne}
        aria-autocomplete="list"
        placeholder={placeholder}
        onChange={(e) => {
          onZmiana(e.target.value);
          setOtwarte(true);
          setWybrany(-1);
        }}
        onFocus={() => setOtwarte(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setOtwarte(true);
            const ile = dopasowane.length;
            if (ile === 0) return;
            setWybrany((i) =>
              e.key === 'ArrowDown' ? (i + 1 >= ile ? 0 : i + 1) : i - 1 < 0 ? ile - 1 : i - 1,
            );
            return;
          }
          if (e.key === 'Enter' && widoczne && wybrany >= 0) {
            // ⚠️ BEZ TEGO ENTER ZAPISUJE FORMULARZ, zamiast wstawić podświetloną podpowiedź.
            e.preventDefault();
            wybierz(dopasowane[wybrany]!.code);
            return;
          }
          if (e.key === 'Escape' && widoczne) {
            /**
             * ⚠️ ESCAPE ZAMYKA LISTĘ, A NIE CAŁE OKNO EDYCJI. Radix słucha `keydown` na
             * `document`, więc sam `stopPropagation` na zdarzeniu Reacta go nie zatrzyma —
             * trzeba przerwać propagację zdarzenia NATYWNEGO. Przy zamkniętej liście Escape
             * zamyka okno jak wszędzie i tak ma zostać.
             */
            e.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
            setOtwarte(false);
            setWybrany(-1);
          }
        }}
      />

      {widoczne && (
        <>
          {/* Klik poza listą ją zamyka — jak w wyborze towaru w panelu planogramu. */}
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-30 cursor-default"
            onClick={() => setOtwarte(false)}
          />
          <ul
            role="listbox"
            className="absolute top-full z-40 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg"
          >
            {dopasowane.map((c, i) => (
              <li key={c.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === wybrany}
                  onClick={() => wybierz(c.code)}
                  onMouseEnter={() => setWybrany(i)}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 rounded px-2 py-1.5 text-left text-sm hover:bg-accent',
                    i === wybrany && 'bg-accent',
                  )}
                >
                  <span className="min-w-0 truncate">{c.code}</span>
                  {/* Licznik użycia mówi, który wariant pisowni jest w tej firmie właściwy. */}
                  <span className="shrink-0 text-[10px] text-muted-foreground">
                    {c.wUzyciu > 0
                      ? t('emp.costCenterInUse').replace('{n}', String(c.wUzyciu))
                      : t('emp.costCenterFromDict')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
